import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Sun, Moon, MessageCircle, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "../Components/Footer";
import Logo from "../Assets/Homeeframes/Homeeframes Liner (1).png";

const PHONE = "+919605204999";
const EMAIL = "info@homeeframes.com"; // replace with actual email
const WHATSAPP_MESSAGE = "Hi, I'm interested in Homeeframes projects. I'd like to know more.";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: "easeOut" },
  }),
};

const contactChannels = [
  {
    id: "whatsapp",
    icon: MessageCircle,
    label: "WhatsApp",
    value: PHONE,
    description: "Chat with us instantly on WhatsApp. We reply within minutes.",
    cta: "Open WhatsApp",
    href: `https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`,
    color: "#25D366",
  },
  {
    id: "phone",
    icon: Phone,
    label: "Call Us",
    value: PHONE,
    description: "Speak directly with our team for a personal consultation.",
    cta: "Call Now",
    href: `tel:${PHONE}`,
    color: "#FEF200",
  },
  {
    id: "email",
    icon: Mail,
    label: "Email",
    value: EMAIL,
    description: "Send us a detailed message and we'll get back to you within 24 hours.",
    cta: "Send Email",
    href: `mailto:${EMAIL}?subject=Project Inquiry - Homeeframes`,
    color: "#60a5fa",
  },
];

const ContactPage = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const bg = isDarkMode ? "bg-[#111111] text-white" : "bg-white text-black";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const cardBg = isDarkMode ? "bg-[#1a1a1a]" : "bg-[#F5F5F5]";
  const divider = isDarkMode ? "bg-gray-800" : "bg-gray-200";
  const accentLine = isDarkMode ? "bg-[#FEF200]" : "bg-black";

  return (
    <div className={`min-h-screen w-full transition-colors duration-500 ${bg}`}>


      {/* ── HERO ── */}
      <section className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 pt-28 pb-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="max-w-3xl mx-auto flex flex-col items-center gap-6"
        >
          <span className={`text-xs tracking-[0.3em] uppercase font-semibold px-4 py-2 rounded-full ${isDarkMode ? "bg-gray-800 text-[#FEF200]" : "bg-gray-100 text-gray-500"}`}>
            Get In Touch
          </span>
          <h1 className="text-5xl md:text-7xl font-bold leading-tight tracking-tight">
            Let's build something{" "}
            <span className={isDarkMode ? "text-[#FEF200]" : "text-gray-400"}>
              extraordinary.
            </span>
          </h1>
          <p className={`text-lg md:text-xl max-w-xl leading-relaxed ${muted}`}>
            Have a project in mind or just want to explore possibilities?
            Reach out — we'd love to hear from you.
          </p>
        </motion.div>
      </section>

      {/* ── CONTACT CHANNELS ── */}
      <section className={`py-20 px-6 md:px-16 lg:px-28 ${isDarkMode ? "bg-[#0d0d0d]" : "bg-[#F5F5F5]"}`}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-14"
        >
          <p className={`text-xs tracking-[0.3em] uppercase font-semibold mb-3 ${isDarkMode ? "text-[#FEF200]" : "text-gray-400"}`}>
            Reach Us
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Connect With Us</h2>
          <div className={`mx-auto mt-4 h-[2px] w-14 rounded-full ${accentLine}`} />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {contactChannels.map((channel, i) => {
            const Icon = channel.icon;
            return (
              <motion.a
                key={channel.id}
                href={channel.href}
                target={channel.id !== "phone" ? "_blank" : "_self"}
                rel="noopener noreferrer"
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                className={`group flex flex-col gap-5 p-8 rounded-3xl cursor-pointer transition-shadow duration-300 hover:shadow-2xl no-underline ${cardBg}`}
              >
                {/* Icon */}
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${channel.color}20`, border: `1px solid ${channel.color}40` }}
                >
                  <Icon size={24} style={{ color: channel.color }} />
                </div>

                {/* Label */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs tracking-widest uppercase font-semibold" style={{ color: channel.color }}>
                    {channel.label}
                  </span>
                  <span className={`text-lg font-bold tracking-tight ${isDarkMode ? "text-white" : "text-black"}`}>
                    {channel.value}
                  </span>
                </div>

                {/* Divider */}
                <div className={`w-full h-px ${divider}`} />

                {/* Description */}
                <p className={`text-sm leading-relaxed flex-1 ${muted}`}>
                  {channel.description}
                </p>

                {/* CTA */}
                <div className="flex items-center gap-2 font-semibold text-sm group-hover:gap-3 transition-all duration-200" style={{ color: channel.color }}>
                  {channel.cta}
                  <ArrowUpRight size={16} className="group-hover:rotate-12 transition-transform duration-200" />
                </div>
              </motion.a>
            );
          })}
        </div>
      </section>

      {/* ── LOCATION ── */}
      <section className="py-24 px-6 md:px-16 lg:px-28 max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="flex flex-col md:flex-row gap-12 items-center"
        >
          {/* Text */}
          <div className="flex flex-col gap-5 flex-1">
            <p className={`text-xs tracking-[0.3em] uppercase font-semibold ${isDarkMode ? "text-[#FEF200]" : "text-gray-400"}`}>
              Find Us
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Visit Our Office</h2>
            <div className={`flex items-start gap-4 p-6 rounded-2xl ${cardBg}`}>
              <MapPin size={22} className={`mt-0.5 flex-shrink-0 ${isDarkMode ? "text-[#FEF200]" : "text-black"}`} />
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-base">Homeeframes HQ</span>
                <span className={`text-sm leading-relaxed ${muted}`}>
                  Thiruvananthapuram, Kerala, India — 695001
                </span>
              </div>
            </div>
            <div className={`flex items-center gap-4 p-6 rounded-2xl ${cardBg}`}>
              <Phone size={22} className={`flex-shrink-0 ${isDarkMode ? "text-[#FEF200]" : "text-black"}`} />
              <a
                href={`tel:${PHONE}`}
                className={`text-sm font-medium hover:underline ${muted}`}
              >
                {PHONE}
              </a>
            </div>
            <div className={`flex items-center gap-4 p-6 rounded-2xl ${cardBg}`}>
              <Mail size={22} className={`flex-shrink-0 ${isDarkMode ? "text-[#FEF200]" : "text-black"}`} />
              <a
                href={`mailto:${EMAIL}`}
                className={`text-sm font-medium hover:underline ${muted}`}
              >
                {EMAIL}
              </a>
            </div>
          </div>

          {/* CTA Banner */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className={`flex-1 rounded-3xl p-10 flex flex-col gap-6 items-start ${isDarkMode ? "bg-[#FEF200]" : "bg-black"}`}
          >
            <span className={`text-xs tracking-[0.3em] uppercase font-semibold ${isDarkMode ? "text-black/60" : "text-white/60"}`}>
              Ready to start?
            </span>
            <h3 className={`text-3xl font-bold leading-tight ${isDarkMode ? "text-black" : "text-white"}`}>
              Your dream home is one conversation away.
            </h3>
            <p className={`text-sm leading-relaxed ${isDarkMode ? "text-black/70" : "text-white/70"}`}>
              Tap below to open WhatsApp and connect with our team directly.
              No forms, no waiting — just a real conversation.
            </p>
            <a
              href={`https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 px-8 py-3 rounded-full font-bold text-sm tracking-wide transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                isDarkMode ? "bg-black text-[#FEF200]" : "bg-white text-black"
              }`}
            >
              <MessageCircle size={16} />
              Chat on WhatsApp
            </a>
          </motion.div>
        </motion.div>
      </section>

      <Footer isDarkMode={isDarkMode} />
    </div>
  );
};

export default ContactPage;
